import { __decorate } from 'tslib';
import { EntityStore, StoreConfig, guid, QueryEntity, withTransaction, logAction } from '@datorama/akita';
import * as i0 from '@angular/core';
import { Injectable, Pipe, InjectionToken, Inject, APP_INITIALIZER, NgModule } from '@angular/core';
import { map, skip, tap, takeUntil, switchMap, mapTo, catchError, take } from 'rxjs/operators';
import { Subject, throwError, of } from 'rxjs';
import equal from 'fast-deep-equal';
import { urlJoin } from 'url-join-ts';
import * as i3 from '@angular/common/http';
import { HttpClient } from '@angular/common/http';
import { AbstractControl } from '@angular/forms';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import { isDate, toIsoString } from '@ff/utils';

const idKey = 'k';
let LocalizationStore = class LocalizationStore extends EntityStore {
    constructor() {
        super();
    }
    akitaPreAddEntity(loc) {
        return Object.assign(Object.assign({}, loc), { k: loc.k.toLowerCase() });
    }
    akitaPreUpdateEntity(_prev, next) {
        return Object.assign(Object.assign({}, next), { k: next.k.toLowerCase() });
    }
};
LocalizationStore.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: LocalizationStore, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
LocalizationStore.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: LocalizationStore });
LocalizationStore = __decorate([
    StoreConfig({ idKey, resettable: true, name: `ff-localizations-${guid()}` })
], LocalizationStore);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: LocalizationStore, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return []; } });
class LocalizationQuery extends QueryEntity {
    constructor(store) {
        super(store);
        this.store = store;
        this.reloadTrigger$$ = this.selectAll();
    }
    /**
     * transforms key to a string value
     * @param key localization key
     * @param paramMap first argument is used to replace array of localized keys with value from key value pairs
     */
    transform(key, paramMap) {
        const regExp = /%\[([\S]{2,}?)]%/g;
        if (key == null || key === '') {
            throw new Error('transform key can\'t be null or empty');
        }
        if (!regExp.test(key)) {
            throw new Error('invalid format of key ' + key);
        }
        key = key.replace(/[\[\]%]/g, '');
        let result = '';
        const entity = this.getEntity(key.toLowerCase());
        if (entity != null) {
            result = entity.v;
            let interpolateParams;
            if (paramMap != null) {
                interpolateParams = this._interpolateParams(paramMap);
            }
            if (interpolateParams != null) {
                Object.keys(interpolateParams).forEach(paramKey => {
                    const regexp = `{{\s*${paramKey}\s*}}`;
                    const labelRegex = new RegExp(regexp, 'gm');
                    // @ts-ignore //we check it before
                    result = result.replace(labelRegex, interpolateParams[paramKey]);
                });
            }
        }
        else {
            // tslint:disable-next-line:no-console // we need to show in console even in production for better problem solving
            console.warn('Localization key not found: ' + key);
            // show last part of the complex key to ease development
            const parts = key.split('.');
            if (parts.length > 0) {
                result = parts[parts.length - 1]; // this expression always returns a string, can't be undefined
            }
        }
        return result;
    }
    /**
     * return observable that emits value on key change (language was reloaded)
     * @param key localization key
     * @param paramMap first argument is used to replace array of localized keys with value from key value pairs
     */
    transform$(key, paramMap) {
        return this.selectEntity(key).pipe(map(k => { var _a; return this.transform((_a = k === null || k === void 0 ? void 0 : k.k) !== null && _a !== void 0 ? _a : key, paramMap); }));
    }
    _interpolateParams(paramMap) {
        let interpolateParams;
        if (paramMap == null) {
            return interpolateParams;
        }
        if (typeof paramMap === 'string' && paramMap.length > 0) {
            // we accept objects written in the template such as {n:1}, {'n':1}, {n:'v'}
            // which is why we might need to change it to real JSON objects such as {"n":1} or {"n":"v"}
            const validArgs = paramMap
                .replace(/(\')?([a-zA-Z0-9_]+)(\')?(\s)?:/g, '"$2":')
                .replace(/:(\s)?(\')(.*?)(\')/g, ':"$3"');
            try {
                interpolateParams = JSON.parse(validArgs);
            }
            catch (e) {
                throw new SyntaxError(`Wrong parameter in TranslatePipe. Expected a valid Object, received: ${paramMap}`);
            }
        }
        else if (typeof paramMap === 'object' && !Array.isArray(paramMap)) {
            interpolateParams = paramMap;
        }
        return interpolateParams;
    }
}
LocalizationQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: LocalizationQuery, deps: [{ token: LocalizationStore }], target: i0.ɵɵFactoryTarget.Injectable });
LocalizationQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: LocalizationQuery });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: LocalizationQuery, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: LocalizationStore }]; } });

let LanguagesStore = class LanguagesStore extends EntityStore {
    constructor() {
        super({ active: null });
    }
};
LanguagesStore = __decorate([
    StoreConfig({ name: `ff-languages-${guid()}`, resettable: false })
], LanguagesStore);
class LanguagesQuery extends QueryEntity {
    constructor(store) {
        super(store);
        this.store = store;
    }
}
LanguagesQuery.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: LanguagesQuery, deps: [{ token: LanguagesStore }], target: i0.ɵɵFactoryTarget.Injectable });
LanguagesQuery.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: LanguagesQuery });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: LanguagesQuery, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: LanguagesStore }]; } });

class TranslatePipe {
    constructor(_localizationQ, _changeDetectorRef) {
        this._localizationQ = _localizationQ;
        this._changeDetectorRef = _changeDetectorRef;
        this._destroyed$ = new Subject();
        this._cachedValue = '';
        this._isCached = false;
        this._cachedKey = null;
        this._cachedArgs = null;
        this._localizationQ.reloadTrigger$$.pipe(skip(1), tap(() => {
            this._isCached = false;
            this._changeDetectorRef.detectChanges();
        }), takeUntil(this._destroyed$)).subscribe();
    }
    transform(key, paramMap) {
        if (this._cachedKey == null) {
            this._cachedKey = key;
        }
        if (this._cachedKey !== key) {
            this._isCached = false;
            this._cachedKey = key;
        }
        if (!this._deepEquals(this._cachedArgs, paramMap)) {
            this._isCached = false;
            this._cachedArgs = paramMap;
        }
        if (!this._isCached) {
            const result = this._localizationQ.transform(key, paramMap);
            this._cachedValue = result == null ? '' : result;
            this._isCached = true;
        }
        return this._cachedValue;
    }
    ngOnDestroy() {
        this._destroyed$.next();
        this._destroyed$.complete();
    }
    _deepEquals(x, y) {
        return equal(x, y);
    }
}
TranslatePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: TranslatePipe, deps: [{ token: LocalizationQuery }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Pipe });
TranslatePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: TranslatePipe, name: "tr", pure: false });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: TranslatePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'tr',
                    pure: false
                }]
        }], ctorParameters: function () { return [{ type: LocalizationQuery }, { type: i0.ChangeDetectorRef }]; } });

const LocalizationConfigInjectionToken = new InjectionToken('LocalizationModuleConfig');

/**
 * calculate salt from configuration
 */
function getSaltWithQuestionMark(config) {
    if (config == null) {
        return '';
    }
    if (typeof config.urlSalt === 'string') {
        const result = config.urlSalt.trim();
        return result.length > 0 ? '?' + result : result;
    }
    if (typeof config.urlSalt === 'function') {
        const result = config.urlSalt().trim();
        return result.length > 0 ? '?' + result : result;
    }
    return '';
}

var EFileType;
(function (EFileType) {
    EFileType[EFileType["json"] = 0] = "json";
    EFileType[EFileType["js"] = 1] = "js";
})(EFileType || (EFileType = {}));

class LocalizationModuleInitializer {
    constructor(_config, _langQ, _langStore, _locStore, _httpBackend) {
        this._config = _config;
        this._langQ = _langQ;
        this._langStore = _langStore;
        this._locStore = _locStore;
        this._httpBackend = _httpBackend;
    }
    /**
     * main function for initialization of languages and localizations in APP_INITIALIZER
     */
    initializeModule() {
        if (this._config.fileType === EFileType.js && (this._config.localizations == null && this._config.languages == null)) {
            return throwError('not selected required fields in config');
        }
        if (this._config.fileType === EFileType.json &&
            (this._config.defaultLocalizations == null || this._config.localizationFilesUrl == null)) {
            return throwError('not selected required fields in config');
        }
        if (this._config.fileType === EFileType.js) {
            return of(void 0).pipe(switchMap(() => this._loadLanguagesFromJs()), switchMap(() => this._loadLocalizationsOrDefaultFromJs()), mapTo(void 0));
        }
        else {
            return of(void 0).pipe(switchMap(() => this._loadLanguages()), switchMap(() => this._loadLocalizationsOrDefault()), mapTo(void 0));
        }
    }
    _loadLanguages() {
        var _a;
        const salt = getSaltWithQuestionMark(this._config);
        const urls = {
            url1: urlJoin(this._config.localizationFilesUrl, '_languages.json' + salt),
            url2: ((_a = this._config.localizationFilesUrl) === null || _a === void 0 ? void 0 : _a.toLowerCase().startsWith('http'))
                ? null
                : urlJoin(document.baseURI, this._config.localizationFilesUrl, '_languages.json' + salt)
        };
        return of(urls).pipe(tap(() => this._langStore.setLoading(true)), switchMap((u) => {
            return this._httpGetFrom2Urls(u.url1, u.url2).pipe(catchError((err) => {
                console.warn('Could not load languages, returning English as default', err);
                return of([{ id: 'default', isDefault: true, showInList: true, title: 'English' }]);
            }));
        }), 
        // update store
        withTransaction(languages => {
            var _a;
            logAction('_loadLanguages write to store');
            this._langStore.set(languages);
            // select active language
            const activeLang = (_a = languages.find(lng => lng.isDefault && lng.showInList)) !== null && _a !== void 0 ? _a : languages[0];
            if (activeLang != null) {
                this._langStore.setActive(activeLang.id);
            }
            this._langStore.setLoading(false);
        }), mapTo(void 0));
    }
    _loadLanguagesFromJs() {
        return of(void 0).pipe(tap(() => this._langStore.setLoading(true)), withTransaction(() => {
            var _a;
            const languages = this._config.languages;
            this._langStore.set(languages);
            // select active language
            const activeLang = (_a = languages.find(lng => lng.isDefault && lng.showInList)) !== null && _a !== void 0 ? _a : languages[0];
            if (activeLang != null) {
                this._langStore.setActive(activeLang.id);
            }
            this._langStore.setLoading(false);
        }), mapTo(void 0));
    }
    _loadLocalizationsOrDefaultFromJs() {
        var _a, _b;
        // @ts-ignore
        const defaultLocalization = (_b = (_a = this._config) === null || _a === void 0 ? void 0 : _a.localizations['_default.localization']) !== null && _b !== void 0 ? _b : [];
        return of(void 0).pipe(switchMap(() => this._langQ.selectActive().pipe(take(1))), withTransaction((activeLang) => {
            logAction('_loadLocalizationsOrDefault write values to store');
            let activeLocalizatons;
            if (activeLang != null) {
                // @ts-ignore
                // we 100% know that lang will be executed
                activeLocalizatons = this._config.localizations[activeLang.id];
            }
            else {
                activeLocalizatons = defaultLocalization;
            }
            this._locStore.upsertMany(activeLocalizatons);
            this._locStore.setLoading(false);
        }), mapTo(void 0));
    }
    _loadLocalizationsOrDefault() {
        var _a;
        const salt = getSaltWithQuestionMark(this._config);
        const defaultLocalizations = (_a = this._config.defaultLocalizations) !== null && _a !== void 0 ? _a : [];
        return of(void 0).pipe(
        // first, let's set default localizations to store
        withTransaction(() => {
            logAction('_loadLocalizationsOrDefault write default localizations to store');
            this._locStore.set(defaultLocalizations);
            this._locStore.setLoading(true);
        }), 
        // try to get default language
        switchMap(() => this._langQ.selectActive().pipe(take(1))), 
        // if no language is selected, console.warn and stop execution
        switchMap((lang) => {
            if (lang == null || lang.id === 'default') {
                this._locStore.setLoading(false);
                console.warn('_loadLocalizationsOrDefault: no active language found, using default localizations');
                return of({ skip: true, lang: null });
            }
            return of({ skip: false, lang });
        }), 
        // load localization from server
        map((action) => {
            var _a;
            return action.skip
                ? ({ skip: true, url: null })
                : ({ skip: false, url: urlJoin(this._config.localizationFilesUrl, `${(_a = action.lang) === null || _a === void 0 ? void 0 : _a.id}.json` + salt) });
        }), switchMap((action) => {
            if (action.skip || action.url == null) {
                return of(defaultLocalizations);
            }
            const http = new HttpClient(this._httpBackend);
            return http.get(action.url).pipe(catchError(err => {
                console.error('_loadLocalizationsOrDefault: could not get localization file from server, using default...', err);
                // return default localizations to change language to default
                return of(defaultLocalizations);
            }));
        }), withTransaction(localizations => {
            logAction('_loadLocalizationsOrDefault write values to store');
            this._locStore.upsertMany(localizations);
            this._locStore.setLoading(false);
        }), mapTo(void 0));
    }
    /**
     * Tries to get data from url1, and if error, tries to get url2
     * @private
     */
    _httpGetFrom2Urls(url1, url2) {
        const http = new HttpClient(this._httpBackend);
        return of(0).pipe(switchMap(() => http.get(url1).pipe(catchError((err) => {
            console.warn(`can\'t load ${url1}`);
            if (url2 == null) {
                return throwError(err);
            }
            return of('error');
        }))), switchMap((url1Result) => {
            if (url1Result !== 'error') {
                return of(url1Result);
            }
            return http.get(url2 !== null && url2 !== void 0 ? url2 : '');
        }));
    }
}
LocalizationModuleInitializer.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: LocalizationModuleInitializer, deps: [{ token: LocalizationConfigInjectionToken }, { token: LanguagesQuery }, { token: LanguagesStore }, { token: LocalizationStore }, { token: i3.HttpBackend }], target: i0.ɵɵFactoryTarget.Injectable });
LocalizationModuleInitializer.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: LocalizationModuleInitializer });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: LocalizationModuleInitializer, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [LocalizationConfigInjectionToken]
                    }] }, { type: LanguagesQuery }, { type: LanguagesStore }, { type: LocalizationStore }, { type: i3.HttpBackend }];
    } });

/**
 * Load localizations from assets directory
 */
class AssetsLocalizationService {
    constructor(_config, _languagesQuery, _localizationsStore, _languagesStore, _languagesQ, _httpBackend) {
        this._config = _config;
        this._languagesQuery = _languagesQuery;
        this._localizationsStore = _localizationsStore;
        this._languagesStore = _languagesStore;
        this._languagesQ = _languagesQ;
        this._httpBackend = _httpBackend;
    }
    // noinspection DuplicatedCode
    changeLanguage(lang) {
        return of(void 0)
            .pipe(switchMap(() => this._languagesQ.selectAll()), take(1), map((languages) => {
            const matchedLang = languages === null || languages === void 0 ? void 0 : languages.find(x => x.id === (lang === null || lang === void 0 ? void 0 : lang.toLowerCase()));
            if (matchedLang != null) {
                return matchedLang.id;
            }
            console.warn(`${lang} not found, switching to default localization`);
            return 'default';
        }), tap((langCode) => this._languagesStore.setActive(langCode)), switchMap(() => this._loadLocalizations()), mapTo(void 0));
    }
    _loadLocalizations(lngCode) {
        // validation
        if (lngCode == null && this._languagesQ.getActive() == null) {
            throw new Error('loadLocalization: no default or selected language found');
        }
        let defaultLocalizations;
        if (this._config.fileType === EFileType.json && this._config.defaultLocalizations != null) {
            defaultLocalizations = this._config.defaultLocalizations;
        }
        else {
            // @ts-ignore
            defaultLocalizations = this._config.localizations['_default.localization'];
        }
        if (defaultLocalizations == null || defaultLocalizations.length === 0) {
            throw new Error('loadLocalization: defaultLocalizations doesn\'t have any items');
        }
        const getActiveLangCode = () => {
            var _a;
            const result = (_a = this._languagesQuery.getActiveId()) !== null && _a !== void 0 ? _a : (lngCode !== null && lngCode !== void 0 ? lngCode : null);
            if (result == null) {
                throw new Error('loadLocalization: no default or selected language found');
            }
            return result;
        };
        return of(void 0).pipe(map(() => getActiveLangCode()), tap(() => this._localizationsStore.setLoading(true)), switchMap((activeLang) => {
            var _a;
            if (this._config.fileType === EFileType.js) {
                // @ts-ignore
                const selectedLocalizations = (_a = this._config.localizations[activeLang]) !== null && _a !== void 0 ? _a : defaultLocalizations;
                return of(selectedLocalizations);
            }
            else {
                return this._getJsonLocalizations(this._config, activeLang, defaultLocalizations);
            }
        }), map((locStrings) => [locStrings, defaultLocalizations]), tap(([locStrings, defaultStrings]) => {
            // log missing keys
            const defaultKeys = defaultStrings.map(x => x.k.toLowerCase());
            const locKeys = locStrings.map(x => x.k.toLowerCase());
            const missingKeys = defaultKeys.filter(dk => !locKeys.includes(dk));
            if (missingKeys.length > 0) {
                // tslint:disable-next-line:no-console
                console.warn('missing localization keys: ', missingKeys);
            }
        }), map(([locStrings, defaultStrings]) => {
            if (this._config.replaceValueWithDefaultLocalizationIfEmptyString !== true) {
                return [locStrings, defaultStrings];
            }
            // replace empty values from default localization
            const newLocStrings = [...locStrings].map(str => {
                var _a, _b;
                const result = { k: str.k, v: str.v };
                if (result.v === '' || result.v.trim() === '') {
                    result.v = (_b = (_a = defaultStrings.find(x => x.k === result.k)) === null || _a === void 0 ? void 0 : _a.v) !== null && _b !== void 0 ? _b : result.v;
                }
                return result;
            });
            return [newLocStrings, defaultStrings];
        }), withTransaction(([locStrings, defaultStrings]) => {
            logAction('_loadLocalizations, set newly loaded localizations');
            this._localizationsStore.set(defaultStrings);
            this._localizationsStore.upsertMany(locStrings);
            this._localizationsStore.setLoading(false);
        }), catchError(err => {
            this._localizationsStore.setLoading(false);
            return throwError(err);
        }), mapTo(void 0));
    }
    _getJsonLocalizations(config, lng, defaultLocalizations) {
        const salt = getSaltWithQuestionMark(config);
        const url = urlJoin(config.localizationFilesUrl, `${lng}.json${salt}`);
        const httpClient = new HttpClient(this._httpBackend);
        return httpClient.get(url).pipe(catchError(err => {
            console.warn('Can\'t get localizations. Using default vocabulary.', err);
            return of(defaultLocalizations);
        }));
    }
}
AssetsLocalizationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: AssetsLocalizationService, deps: [{ token: LocalizationConfigInjectionToken }, { token: LanguagesQuery }, { token: LocalizationStore }, { token: LanguagesStore }, { token: LanguagesQuery }, { token: i3.HttpBackend }], target: i0.ɵɵFactoryTarget.Injectable });
AssetsLocalizationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: AssetsLocalizationService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: AssetsLocalizationService, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [LocalizationConfigInjectionToken]
                    }] }, { type: LanguagesQuery }, { type: LocalizationStore }, { type: LanguagesStore }, { type: LanguagesQuery }, { type: i3.HttpBackend }];
    } });

class AkitaStoresSingletonHolder {
    constructor() {
        this._singleLocalizationStore = null;
        this._singleLocalizationQuery = null;
        this._singleLanguageStore = null;
        this._singleLanguageQuery = null;
    }
    getLocalizationStore() {
        if (this._singleLocalizationStore == null) {
            this._singleLocalizationStore = new LocalizationStore();
        }
        return this._singleLocalizationStore;
    }
    getLocalizationQuery(store) {
        if (this._singleLocalizationQuery == null) {
            this._singleLocalizationQuery = new LocalizationQuery(store);
        }
        return this._singleLocalizationQuery;
    }
    getLanguagesStore() {
        if (this._singleLanguageStore == null) {
            this._singleLanguageStore = new LanguagesStore();
        }
        return this._singleLanguageStore;
    }
    getLanguagesQuery(store) {
        if (this._singleLanguageQuery == null) {
            this._singleLanguageQuery = new LanguagesQuery(store);
        }
        return this._singleLanguageQuery;
    }
}

function init_app(moduleInitializer) {
    // noinspection UnnecessaryLocalVariableJS
    const result = () => {
        return of(void 0).pipe(switchMap(() => moduleInitializer.initializeModule()), mapTo(void 0)).toPromise();
    };
    return result;
}
// при подключении библиотеки к приложению обнаружилось, что Angular пытается создать несколько копий сервисов
// при подключении к дочерним модулям или в shared-low-module для приложений, поэтому все языковые сервисы принудительно
// делаем Singleton'ами через фабрику
const akitaSingletonHolder = new AkitaStoresSingletonHolder();
class LocalizationModule {
    static forRoot(config) {
        return {
            ngModule: LocalizationModule,
            providers: [
                AssetsLocalizationService,
                LocalizationModuleInitializer,
                {
                    provide: LocalizationConfigInjectionToken,
                    useValue: config
                },
                {
                    provide: APP_INITIALIZER,
                    useFactory: init_app,
                    deps: [LocalizationModuleInitializer],
                    multi: true
                }
            ]
        };
    }
}
LocalizationModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: LocalizationModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
LocalizationModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: LocalizationModule, declarations: [TranslatePipe], exports: [TranslatePipe] });
LocalizationModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: LocalizationModule, providers: [
        { provide: LocalizationStore, useFactory: akitaSingletonHolder.getLocalizationStore },
        { provide: LanguagesStore, useFactory: akitaSingletonHolder.getLanguagesStore },
        { provide: LanguagesQuery, useFactory: akitaSingletonHolder.getLanguagesQuery, deps: [LanguagesStore] },
        { provide: LocalizationQuery, useFactory: akitaSingletonHolder.getLocalizationQuery, deps: [LocalizationStore] }
    ], imports: [[]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: LocalizationModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [TranslatePipe],
                    imports: [],
                    exports: [TranslatePipe],
                    providers: [
                        { provide: LocalizationStore, useFactory: akitaSingletonHolder.getLocalizationStore },
                        { provide: LanguagesStore, useFactory: akitaSingletonHolder.getLanguagesStore },
                        { provide: LanguagesQuery, useFactory: akitaSingletonHolder.getLanguagesQuery, deps: [LanguagesStore] },
                        { provide: LocalizationQuery, useFactory: akitaSingletonHolder.getLocalizationQuery, deps: [LocalizationStore] }
                    ],
                }]
        }] });

class ToDateFormatter {
    constructor() {
        this.name = 'toDate';
    }
    formatError(errorValue) {
        if (errorValue == null) {
            return '';
        }
        if (isDate(errorValue)) {
            return format(parseISO(toIsoString(errorValue)), 'dd/MM/yyyy');
        }
        return JSON.stringify(errorValue);
    }
}

class ValidationLocalizationService {
    constructor(_config, _localizationQuery) {
        this._config = _config;
        this._localizationQuery = _localizationQuery;
        /**
         * A collection of default error formatters that come with the validation library
         */
        this.baseErrorFormatters = [
            new ToDateFormatter()
        ];
        this._cachedLocalizationErrorMessages = null;
    }
    /**
     * Get default localized error messages for the form validation
     * @param options additional errors to include in the collection, also use it to override default messages, and custom error formatters
     * @example validationService.getLocalizedErrorMessages({
     *    overridingErrors: {
     *      required: 'xxx.yyy.my-super-required-field-error',
     *      my-custom-validator-key: 'xxx.yyy.my-custom-validator-error-message'
     *    }
     *    }
     * });
     */
    getLocalizedErrorMessages(options) {
        var _a, _b;
        const overridingErrors = (_a = options === null || options === void 0 ? void 0 : options.overridingErrors) !== null && _a !== void 0 ? _a : null;
        const errorFormatters = this._getErrorFormatters(options === null || options === void 0 ? void 0 : options.errorFormatters);
        const keysOverridden = overridingErrors != null ? Object.keys(overridingErrors) : [];
        const defaultKeys = (_b = this._config.defaultLocalizationKeys) !== null && _b !== void 0 ? _b : {};
        // noinspection PointlessBooleanExpressionJS
        const result = Object.keys(defaultKeys)
            .filter(key => keysOverridden.includes(key) === false)
            // @ts-ignore I didn't get how to make defaultKeys[key] work correct
            .map((key) => this.createLocalizedErrorMessage(key, defaultKeys[key], errorFormatters));
        if (overridingErrors != null && keysOverridden.length > 0) {
            // @ts-ignore overridingErrors[k] can never return undefined
            result.push(...keysOverridden.map(k => this.createLocalizedErrorMessage(k, overridingErrors[k], errorFormatters)));
        }
        return result;
    }
    /**
     * Return error messages for specific errors in a control
     * @param controlOrErrors Angular abstract control or control errors
     * @param errorMessages a collection of ErrorMessages to process
     * @param options Label for the error message
     * @example
     * const firstName = new FormControl(null, [Validators.required]);
     * public onSaveClick(): void {
     *   const errors = validationLocalizationService.getControlErrors(firstName);
     *   // errors = [{ key: 'required', errorMessage: 'The field is required' }]
     * }
     */
    // tslint:disable-next-line:max-line-length
    getControlErrors(controlOrErrors, errorMessages, options) {
        var _a;
        // create cached error messages
        if (this._cachedLocalizationErrorMessages == null) {
            this._cachedLocalizationErrorMessages = this.getLocalizedErrorMessages();
        }
        const errorMessagesInner = errorMessages !== null && errorMessages !== void 0 ? errorMessages : this.getLocalizedErrorMessages();
        if (controlOrErrors == null) {
            return [];
        }
        const controlErrors = (controlOrErrors instanceof AbstractControl) ? controlOrErrors.errors : controlOrErrors;
        if (controlErrors == null) {
            return [];
        }
        // main logic
        const label = (_a = options === null || options === void 0 ? void 0 : options.label) !== null && _a !== void 0 ? _a : '';
        const result = [];
        Object.keys(controlErrors).forEach(key => {
            var _a;
            const errorMessage = (_a = errorMessagesInner.find(x => key === x.error)) !== null && _a !== void 0 ? _a : null;
            // if no errorMessage is found in provided collection, notify user via console and add unknown error
            if (errorMessage == null) {
                const errMsgArrayToString = errorMessagesInner.map(x => x.error).join(', ');
                let consoleError = `'${key}' is not found in provided errorMessages: [${errMsgArrayToString}]`;
                if (label != null && label.trim() !== '') {
                    consoleError += `, label: '${label}'`;
                }
                console.error(consoleError);
                result.push({ key, errorMessage: 'Error' });
                return;
            }
            const errorMessageFormatted = errorMessage.format(label !== null && label !== void 0 ? label : '', controlErrors[key]);
            result.push({ key, errorMessage: errorMessageFormatted });
        });
        return result;
    }
    /**
     * Return only localized error messages for the control
     * @param controlOrErrors Angular abstract control or control errors
     * @param errorMessages a collection of ErrorMessages to process
     * @param options Label for the error message
     * @example
     * const firstName = new FormControl(null, [Validators.required]);
     * public onSaveClick(): void {
     *   const errors = validationLocalizationService.getControlErrors(firstName);
     *   // errors = ['The field is required'];
     * }
     */
    getControlErrorsLocalizedStrings(controlOrErrors, errorMessages, options) {
        const errors = this.getControlErrors(controlOrErrors, errorMessages, options);
        return errors.map(x => x.errorMessage);
    }
    /**
     * Creates an ErrorMessage instance with provided errorKey (e.g. 'maxlength')
     * and localization key (e.g. 'remittance.xxx.yyy.error-messages.my-maxlength')
     */
    createLocalizedErrorMessage(errorKey, localizationKey, errorFormatters) {
        const valueFn = () => this._localizationQuery.transform(localizationKey);
        // noinspection UnnecessaryLocalVariableJS
        const result = {
            error: errorKey,
            format: (label, error) => this.formatErrorMessage(valueFn, { label, error }, errorFormatters)
        };
        return result;
    }
    /**
     * formats error messages like 'The length of {{ label }} should be grater than {{ error.minLength }}
     * and not contain {{ error.restrictedCharacters }}'
     */
    // tslint:disable-next-line:no-any max-line-length
    formatErrorMessage(localizationValue, options, errorFormatters) {
        var _a, _b;
        const label = (_a = options === null || options === void 0 ? void 0 : options.label) !== null && _a !== void 0 ? _a : null;
        const error = (_b = options === null || options === void 0 ? void 0 : options.error) !== null && _b !== void 0 ? _b : null;
        if (localizationValue == null) {
            throw new Error('localizationValue is null');
        }
        const innerLocalizationValue = typeof localizationValue === 'function' ? localizationValue() : localizationValue;
        if (label == null && error == null) {
            return innerLocalizationValue;
        }
        let result = innerLocalizationValue;
        if (label != null) {
            const labelRegex = /{{\s*label\s*}}/gm;
            result = result.replace(labelRegex, label);
        }
        if (error != null) {
            const errorRegex = /{{(:[a-zA-Z0-9]+)?\s*error\.?([a-zA-Z0-9.]*)\s*}}/gm;
            result = result.replace(errorRegex, (match, errFormatterName, errorGrp) => {
                // get formatter function
                const formatterFn = this._getErrorFormatterFn(errorFormatters, errFormatterName);
                // if the text is {{ error }}, return error.toString()
                if (errorGrp === '') {
                    const formatterError = formatterFn(error);
                    return (typeof formatterError === 'object') ? JSON.stringify(formatterError) : formatterError.toString();
                }
                const errorValue = this.getNestedObjectValueByString(error, errorGrp);
                if (errorValue == null) {
                    return match;
                }
                const formatterError1 = formatterFn(errorValue);
                // noinspection UnnecessaryLocalVariableJS
                const fnResult = typeof formatterError1 === 'object' ? JSON.stringify(formatterError1) : formatterError1.toString();
                return fnResult;
            });
        }
        return result;
    }
    // https://stackoverflow.com/a/6491621
    // tslint:disable-next-line:no-any
    getNestedObjectValueByString(o, s) {
        try {
            s = s.replace(/\[(\w+)]/g, '.$1'); // convert indexes to properties
            s = s.replace(/^\./, ''); // strip a leading dot
            const a = s.split('.');
            for (let i = 0, n = a.length; i < n; ++i) {
                const k = a[i];
                // @ts-ignore
                if (k in o) {
                    // @ts-ignore
                    o = o[k];
                }
                else {
                    return null;
                }
            }
            return o;
        }
        catch (e) {
            return null;
        }
    }
    /**
     * Merges custom formatters into base formatters
     * @param errorFormatters overriding error formatters
     */
    _getErrorFormatters(errorFormatters) {
        const result = [...this.baseErrorFormatters];
        // override base formatters with custom
        if (errorFormatters != null && errorFormatters.length > 0) {
            errorFormatters.forEach(customFormatter => {
                // search with same name
                const existingFormatterIndex = result.findIndex(x => x.name === customFormatter.name);
                if (existingFormatterIndex >= 0) {
                    result[existingFormatterIndex] = customFormatter;
                }
                else {
                    result.push(customFormatter);
                }
            });
        }
        return result;
    }
    /**
     * searches for a format function in the list and return it
     */
    // tslint:disable-next-line:no-any max-line-length
    _getErrorFormatterFn(errorFormatters, name) {
        var _a;
        // if there's no name then just return the value itself
        if (name == null) {
            return errorValue => errorValue;
        }
        name = name.replace(':', '');
        // otherwise search for a formatter and apply it
        const formatter = (_a = errorFormatters.find(x => x.name === name)) !== null && _a !== void 0 ? _a : null;
        if (formatter == null) {
            console.error(name + ' error formatter was not found. Please include it into getLocalizedErrorMessages function');
            return errorValue => errorValue;
        }
        return formatter.formatError;
    }
}
ValidationLocalizationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ValidationLocalizationService, deps: [{ token: LocalizationConfigInjectionToken }, { token: LocalizationQuery }], target: i0.ɵɵFactoryTarget.Injectable });
ValidationLocalizationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ValidationLocalizationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: ValidationLocalizationService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [LocalizationConfigInjectionToken]
                    }] }, { type: LocalizationQuery }];
    } });

/*
 * Public API Surface of localization
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AssetsLocalizationService, EFileType, LanguagesQuery, LocalizationConfigInjectionToken, LocalizationModule, LocalizationQuery, TranslatePipe, ValidationLocalizationService, init_app };
